import { MAX_EMERGENCY_CONTACTS, updatePersonSchema, type Person, type UpdatePerson } from '@vigeo/shared';

export type PersonDetailsSection = 'contact' | 'assignment' | 'emr';
export interface DetailField {
  readonly path: string;
  readonly label: string;
  readonly type?: 'email' | 'tel' | 'date';
  readonly choices?: ReadonlyArray<readonly [string, string]>;
  readonly multiline?: boolean;
}

export const detailSectionTitles: Record<PersonDetailsSection, string> = {
  contact: 'Personal and contact details', assignment: 'Employment and assignment', emr: 'EMR setup',
};

/** One slot per contact the record can hold (`MAX_EMERGENCY_CONTACTS`), so a third contact can be seen and edited. */
const emergencySlots = Array.from({ length: MAX_EMERGENCY_CONTACTS }, (_, index) => index);
const emergencyFields = emergencySlots.flatMap((index): DetailField[] => [
  { path: `contact.emergencyContacts.${index}.name`, label: `Emergency contact ${index + 1} name` },
  { path: `contact.emergencyContacts.${index}.relationship`, label: `Emergency contact ${index + 1} relationship` },
  { path: `contact.emergencyContacts.${index}.phone`, label: `Emergency contact ${index + 1} phone`, type: 'tel' },
  { path: `contact.emergencyContacts.${index}.address`, label: `Emergency contact ${index + 1} address` },
]);

export const detailFields: Record<PersonDetailsSection, readonly DetailField[]> = {
  contact: [
    { path: 'name.givenName', label: 'First name' },
    { path: 'name.middleName', label: 'Middle name / initial' },
    { path: 'name.familyName', label: 'Last name' },
    { path: 'name.preferredName', label: 'Preferred name' },
    { path: 'identity.previousName', label: 'Previous / maiden name' },
    { path: 'identity.identityVerified', label: 'Identity verification', choices: [['', 'Not recorded'], ['true', 'Verified'], ['false', 'Pending HR verification']] },
    { path: 'contact.email', label: 'Personal email', type: 'email' },
    { path: 'contact.phone', label: 'Cell phone', type: 'tel' },
    { path: 'contact.homePhone', label: 'Home phone', type: 'tel' },
    { path: 'contact.alternatePhone', label: 'Alternate phone', type: 'tel' },
    { path: 'contact.preferredCallTime', label: 'Preferred call time' },
    { path: 'contact.address.streetAddress', label: 'Street address' },
    { path: 'contact.address.addressLine2', label: 'Address line 2' },
    { path: 'contact.address.city', label: 'City' },
    { path: 'contact.address.state', label: 'State' },
    { path: 'contact.address.postalCode', label: 'ZIP / postal code' },
    { path: 'contact.address.country', label: 'Country' },
    ...emergencyFields,
  ],
  assignment: [
    { path: 'assignment.employerName', label: 'Actual employer or contracting entity' },
    { path: 'assignment.employerResponsibility', label: 'Employer responsible for employment documents', choices: [['', 'Not confirmed'], ['vigeo', 'VIGEO is the employer'], ['other', 'Another organization / independent business']] },
    { path: 'assignment.assignedDuties', label: 'Assigned duties and service scope', multiline: true },
    { path: 'assignment.positionTitle', label: 'Position title' },
    { path: 'assignment.department', label: 'Department' },
    { path: 'assignment.employmentClassification', label: 'Employment classification', choices: [['', 'Not recorded'], ['w2', 'W2 employee'], ['1099', '1099 contractor']] },
    { path: 'assignment.employmentType', label: 'Employment type / schedule' },
    { path: 'assignment.serviceArea', label: 'Service area' },
    { path: 'assignment.supervisorName', label: 'Assigned supervisor' },
    { path: 'hireDate', label: 'First day / hire date', type: 'date' },
  ],
  emr: [
    { path: 'emr.status', label: 'EMR status', choices: [['not-started', 'Not started'], ['in-progress', 'In progress'], ['provisioned', 'Provisioned'], ['disabled', 'Disabled']] },
    { path: 'emr.accountIdentifier', label: 'EMR account identifier' },
    { path: 'emr.notes', label: 'EMR notes', multiline: true },
  ],
};

function atPath(person: Person, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) =>
    value !== null && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined,
  person);
}

/** The values of any list of fields, as the strings a form holds. The employee profile's groups (#585) read through this too. */
export function detailValuesOf(person: Person, fields: readonly DetailField[]): Record<string, string> {
  return Object.fromEntries(fields.map(({ path }) => {
    const value = atPath(person, path);
    if (path === 'emr.status') return [path, String(value ?? 'not-started')];
    if (path === 'hireDate') return [path, typeof value === 'string' ? value.slice(0, 10) : ''];
    return [path, value == null ? '' : String(value)];
  }));
}

export function personDetailsValues(person: Person, section: PersonDetailsSection): Record<string, string> {
  return detailValuesOf(person, detailFields[section]);
}

function setPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const [key, ...rest] = path.split('.');
  if (key === undefined) return;
  if (rest.length === 0) { target[key] = value; return; }
  const nested = (target[key] ?? {}) as Record<string, unknown>;
  target[key] = nested;
  setPath(nested, rest.join('.'), value);
}

/** Only edited fields are sent; untouched dates, sibling fields and source answers survive. */
export function personDetailsPatch(
  original: Readonly<Record<string, string>>,
  draft: Readonly<Record<string, string>>,
  section: PersonDetailsSection,
): UpdatePerson {
  return detailPatchOf(original, draft, detailFields[section]);
}

/** The same sparse patch for any list of fields. Throws the shared schema's error when a value is refused. */
export function detailPatchOf(
  original: Readonly<Record<string, string>>,
  draft: Readonly<Record<string, string>>,
  fields: readonly DetailField[],
): UpdatePerson {
  const patch: Record<string, unknown> = {};
  let emergencyChanged = false;
  for (const { path } of fields) {
    const value = (draft[path] ?? '').trim();
    if (value === (original[path] ?? '')) continue;
    if (path.startsWith('contact.emergencyContacts.')) { emergencyChanged = true; continue; }
    const next = path === 'identity.identityVerified' ? (value === '' ? null : value === 'true')
      : path === 'hireDate' ? (value === '' ? null : `${value}T00:00:00.000Z`)
      : path.startsWith('name.') || path === 'contact.email' ? value
      : value === '' ? null : value;
    setPath(patch, path, next);
  }
  if (emergencyChanged) {
    const contacts = emergencySlots.map((index) => Object.fromEntries(
      ['name', 'relationship', 'phone', 'address'].map((field) =>
        [field, (draft[`contact.emergencyContacts.${index}.${field}`] ?? '').trim()]),
    )).filter((contact) => Object.values(contact).some((value) => value !== ''));
    setPath(patch, 'contact.emergencyContacts', contacts);
  }
  return updatePersonSchema.parse(patch);
}
